import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Flame } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Button, Card, Empty, ErrorNote, Field, inputClass, Spinner } from "@/components/ui";
import { HotToggle } from "@/components/HotToggle";
import { PhoneLink } from "@/components/PhoneLink";
import { useToast } from "@/components/Toast";
import { api, type DeadReason, type LeadRow, type LeadStage } from "@/lib/api";
import { formatAcres, formatAgo, formatDue, isActionable, shown } from "@/lib/format";
import { leadHref, listId } from "@/lib/leadList";
import {
  BOARD_STAGES,
  DEAD_REASON_HELP,
  DEAD_REASON_LABEL,
  DEAD_REASONS,
  canMove,
  needsDeadReason,
  needsNextAction,
  STAGE_HELP,
  STAGE_LABEL,
  stageTone,
  SUGGESTION_LABEL,
  SUGGESTION_STATES,
  suggestionTone,
} from "@/lib/stages";
import { cn } from "@/lib/utils";
import { useModalKeys } from "@/lib/dialog";
import { LEAD_SORTS, LEADS_SORT_KEY, matchSort, readStoredSort, storeSort } from "@/lib/sorts";

/**
 * §8.1's manual path. A bulk import is deliberately NOT auto-triaged — a 652-row backfill is not 652
 * things that just happened — so those leads need a way to be triaged on purpose. This is it.
 *
 * 25 per click, matching the API's cap. The limit is the point rather than a UI nicety: it is what
 * keeps one click on a "select all" checkbox from turning a backfill into the month's budget.
 */
const BULK_TRIAGE_MAX = 25;

const COLUMNS: { key: string; label: string; sortable?: string; className?: string }[] = [
  { key: "select", label: "", className: "w-8" },
  // v2.2 P3.1: the flame, first after the checkbox; sortable like any column.
  { key: "hot", label: "", sortable: "hot", className: "w-7" },
  { key: "name", label: "Name", sortable: "name" },
  { key: "phone", label: "Phone" },
  { key: "market", label: "Market", sortable: "market" },
  { key: "county", label: "County", sortable: "county" },
  { key: "acres", label: "Acres", sortable: "acres", className: "text-right" },
  { key: "property_type", label: "Type" },
  { key: "stage", label: "Stage", sortable: "stage" },
  { key: "tier", label: "Tier", sortable: "tier" },
  { key: "score", label: "Score", sortable: "score", className: "text-right" },
  { key: "reply_class", label: "Reply" },
  // D-35: what the agent layer did with this lead, in one word. Without it, "auto-applied" and
  // "nobody has looked at it" are indistinguishable on the table that is supposed to be the worklist.
  { key: "suggestion", label: "Suggestion" },
  { key: "next_action", label: "Next action" },
  { key: "due", label: "Due", sortable: "next_action_due" },
  { key: "touched", label: "Touched", sortable: "last_touched_at" },
  { key: "campaign", label: "Campaign", sortable: "campaign" },
];

/**
 * What a lead is called.
 *
 * `alias` first: a pipeline row's name is its deal — "Sheepneck 36" — and its contact holds a
 * *description* of the seller ("Cliff (heir); executor Brad Rogers"), which listed as a person's name
 * read as though the CRM had misparsed something. An ordinary SC lead has no alias and is named by its
 * person, as before.
 */
function leadName(l: LeadRow): string {
  const person = [l.first_name, l.last_name].filter(Boolean).join(" ");
  return l.alias ?? l.entity_name ?? (person || "(unnamed)");
}

/**
 * Asks for whatever the target stage requires before it will accept the move (§6.1, §7.3).
 *
 * Two requirements, and both are refusals on the server rather than UI politeness: a live stage needs a
 * next action and a due date, and `dead` needs one of the five reasons (D-31). A dialog that guessed
 * either would be putting a value into the §12 suppression export or the loss analysis that nobody chose.
 */
function StageMoveModal({
  lead,
  target,
  onClose,
  onMoved,
}: {
  lead: LeadRow;
  target: LeadStage;
  onClose: () => void;
  onMoved?: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const dying = needsDeadReason(target);
  const wantsAction = needsNextAction(target);
  const [reason, setReason] = useState<DeadReason | null>(null);
  const [action, setAction] = useState(`Follow up after moving to ${STAGE_LABEL[target]}`);
  const [due, setDue] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 16);
  });

  const move = useMutation({
    mutationFn: () =>
      api.patchLead(lead.id, {
        stage: target,
        ...(dying ? { dead_reason: reason } : {}),
        ...(wantsAction
          ? { next_action: action, next_action_due: new Date(due).toISOString() }
          : {}),
      }),
    onSuccess: () => {
      toast.push(`${leadName(lead)} moved to ${STAGE_LABEL[target]}`);
      void qc.invalidateQueries();
      onMoved?.();
      onClose();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const ready = dying ? reason !== null : Boolean(action.trim() && due);
  // v2.3 P1.4: focus lands on the next-action input, Tab stays inside, Esc closes from anywhere.
  const box = useRef<HTMLDivElement>(null);
  useModalKeys(box, onClose);
  const submit = () => {
    if (ready && !move.isPending) move.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={dying ? "Reason required" : "Next action required"}
      data-testid={dying ? "dead-reason-modal" : "next-action-modal"}
    >
      <div ref={box} className="w-full max-w-md rounded-[var(--radius-card)] border bg-surface p-4">
        <h2 className="text-sm font-semibold">
          {dying ? "Why is this lead dead?" : `${STAGE_LABEL[target]} needs a next action`}
        </h2>
        <p className="mt-1 mb-3 text-xs text-fg-muted">
          {dying
            ? "One reason, required. Dead with no reason is the CRM forgetting rather than recording — and `unsubscribed` feeds the suppression export."
            : STAGE_HELP[target] +
              " Every live stage carries a next action and a due date, so nothing sits in the pipeline without someone owning the next move."}
        </p>
        {dying ? (
          <div className="flex flex-wrap gap-1.5" data-testid="dead-reason-chips">
            {DEAD_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                title={DEAD_REASON_HELP[r]}
                data-testid={`dead-reason-${r}`}
                aria-pressed={reason === r}
                onClick={() => setReason(r)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs",
                  reason === r
                    ? "border-danger bg-danger/15 font-medium text-danger"
                    : "text-fg-muted hover:bg-surface-2",
                )}
              >
                {DEAD_REASON_LABEL[r]}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Next action" required>
              <input
                data-autofocus
                value={action}
                onChange={(e) => setAction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submit();
                  }
                }}
                className={inputClass}
                data-testid="next-action-input"
              />
            </Field>
            <Field label="Due" required>
              <input
                type="datetime-local"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submit();
                  }
                }}
                className={inputClass}
                data-testid="next-action-due"
              />
            </Field>
          </div>
        )}
        {dying && reason && (
          <p className="mt-2 text-[11px] text-fg-muted">{DEAD_REASON_HELP[reason]}</p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready || move.isPending}
            onClick={submit}
            data-testid={dying ? "dead-reason-save" : "next-action-save"}
          >
            {move.isPending ? "Saving…" : `Move to ${STAGE_LABEL[target]}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * §7.3's board. Four lanes, not five: `dead` is excluded on purpose (D-32).
 *
 * The v2 model has five stages, but a Kanban is a place where work is moved forward, and a "Dead" lane
 * is a place work goes to stop — one that would grow without bound and never be dragged out of, since
 * the only way back is Revive. Archived leads have their own page, with the reason filter that makes
 * them worth looking at. A lane would have been a bin.
 */
function Kanban({ leads, list }: { leads: LeadRow[]; list: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [dragging, setDragging] = useState<LeadRow | null>(null);
  const [pending, setPending] = useState<{ lead: LeadRow; target: LeadStage } | null>(null);
  // v2.3 P1.4: a real drop on an ILLEGAL lane never fires — the lane does not preventDefault its
  // dragover, so the browser refuses the drop — so the refusal is said when the drag ends over it.
  const overLane = useRef<LeadStage | null>(null);
  const dropped = useRef(false);

  const move = useMutation({
    mutationFn: ({ lead, target }: { lead: LeadRow; target: LeadStage }) =>
      api.patchLead(lead.id, { stage: target }),
    onSuccess: (_d, v) => {
      toast.push(`${leadName(v.lead)} moved to ${STAGE_LABEL[v.target]}`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const hotMove = useMutation({
    mutationFn: ({ id, hot }: { id: string; hot: boolean }) => api.patchLead(id, { hot }),
    onSuccess: (_d, v) => {
      toast.push(v.hot ? "Marked hot" : "Unmarked hot");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  function refuse(lead: LeadRow, target: LeadStage) {
    toast.push(`${STAGE_LABEL[lead.stage]} → ${STAGE_LABEL[target]} is not allowed`, "danger");
  }

  function drop(target: LeadStage) {
    dropped.current = true;
    const lead = dragging;
    setDragging(null);
    if (!lead || lead.stage === target) return;
    if (!canMove(lead.stage, target)) {
      refuse(lead, target);
      return;
    }
    // A live stage needs a next action, so ask for one instead of guessing (§7.3).
    if (needsNextAction(target)) setPending({ lead, target });
    else move.mutate({ lead, target });
  }

  return (
    <>
      {pending && (
        <StageMoveModal
          lead={pending.lead}
          target={pending.target}
          onClose={() => setPending(null)}
        />
      )}
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="kanban">
        {BOARD_STAGES.map((stage) => {
          const inLane = leads.filter((l) => l.stage === stage);
          const legal = !dragging || canMove(dragging.stage, stage);
          return (
            <div
              key={stage}
              data-testid={`lane-${stage}`}
              data-droppable={legal ? "true" : "false"}
              onDragEnter={() => {
                overLane.current = stage;
              }}
              onDragOver={(e) => {
                overLane.current = stage;
                if (legal) {
                  e.preventDefault();
                  const dt = e.dataTransfer as DataTransfer | null;
                  if (dt) dt.dropEffect = "move";
                }
              }}
              // v2.3 P1.4: preventDefault on EVERY drop. Without it the browser's default drop action
              // ran — navigating to whatever URL the drag carried — which unmounted the next-action
              // modal and left the lead where it was. A synthetic test drop carries no URL, which is
              // why the suite was green and a real mouse was not.
              onDrop={(e) => {
                e.preventDefault();
                drop(stage);
              }}
              className={cn(
                "flex w-56 shrink-0 flex-col rounded-[var(--radius-card)] border bg-surface",
                dragging && !legal && "opacity-40",
                dragging && legal && stage !== dragging.stage && "border-brand/60",
              )}
            >
              <header className="border-b px-2 py-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold">{STAGE_LABEL[stage]}</span>
                  <span className="text-[11px] tabular-nums text-fg-muted">{inLane.length}</span>
                </div>
                {/* The definition travels with the lane, so nobody has to open a document to decide
                    whether a lead belongs here (D-31). */}
                <p className="mt-0.5 text-[10px] leading-snug text-fg-muted">{STAGE_HELP[stage]}</p>
              </header>
              <ul className="flex-1 space-y-1 p-1.5">
                {inLane.map((l) => (
                  <li key={l.id}>
                    <div
                      draggable
                      onDragStart={(e) => {
                        // The payload is the lead id, never a URL a browser could navigate to. A
                        // synthetic event (a test's dispatchEvent) carries no DataTransfer at all.
                        const dt = e.dataTransfer as DataTransfer | null;
                        if (dt) {
                          dt.setData("text/plain", l.id);
                          dt.effectAllowed = "move";
                        }
                        overLane.current = null;
                        dropped.current = false;
                        setDragging(l);
                      }}
                      onDragEnd={() => {
                        const over = overLane.current;
                        if (!dropped.current && over && over !== l.stage && !canMove(l.stage, over)) {
                          refuse(l, over);
                        }
                        setDragging(null);
                      }}
                      data-testid={`card-${l.id}`}
                      className="cursor-grab rounded border bg-bg p-1.5 active:cursor-grabbing"
                    >
                      <div className="flex items-center gap-1">
                        <Link
                          to={leadHref(l.id, list, leads.indexOf(l))}
                          // v2.3 P1.4: a link is natively draggable and carries its href; a drag that
                          // started on the name dropped that URL on the lane. The card is what drags.
                          draggable={false}
                          className="block min-w-0 flex-1 truncate text-xs font-medium hover:underline"
                        >
                          {leadName(l)}
                        </Link>
                        <HotToggle
                          hot={l.hot ?? false}
                          onToggle={(hot) => hotMove.mutate({ id: l.id, hot })}
                          testId={`kanban-hot-${l.id}`}
                        />
                      </div>
                      <div className="mt-1 flex items-center gap-1">
                        {l.tier && <Badge tone="muted">{l.tier}</Badge>}
                        {l.parcel_acres && (
                          <span className="text-[11px] text-fg-muted">
                            {formatAcres(l.parcel_acres)}
                          </span>
                        )}
                        {l.stale && <Badge tone="warn">stale</Badge>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * v2.10 P4.2: the phone's Leads — a card per lead instead of the table (search, sort and filters stay in the
 * header), and stage chips + that list instead of the Kanban lanes (no drag on a phone: a stage is changed
 * on the lead page). Shown below 768 px only.
 */
function LeadCards({ leads, list, offset }: { leads: LeadRow[]; list: string; offset: number }) {
  if (leads.length === 0) {
    return (
      // Its own words: the table's "No leads match." is a different element tests (and readers) look for.
      <div className="md:hidden" data-testid="lead-cards-empty">
        <Empty title="Nobody here matches." />
      </div>
    );
  }
  return (
    <ul className="divide-y rounded-[var(--radius-card)] border bg-surface md:hidden" data-testid="lead-cards">
      {leads.map((l, index) => (
        <li key={l.id} className="px-3 py-2" data-testid="lead-card">
          <div className="flex items-center gap-2">
            <Link
              data-testid="lead-card-link"
              to={leadHref(l.id, list, offset + index)}
              className="min-w-0 flex-1 truncate text-sm font-medium hover:underline"
            >
              {leadName(l)}
            </Link>
            {l.hot && <Flame className="size-4 text-orange-600" aria-label="hot" />}
            <Badge tone={stageTone(l.stage)}>{STAGE_LABEL[l.stage]}</Badge>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-fg-muted">
            <PhoneLink e164={l.phone_e164} leadId={l.id} surface="lead-cards" />
            <span>{[shown(l.parcel_county), l.parcel_acres != null ? `${formatAcres(l.parcel_acres)} ac` : null].filter((x) => x && x !== "—").join(" · ")}</span>
          </div>
          {l.next_action && (
            <p className="mt-0.5 truncate text-xs">
              {l.next_action} <span className="text-fg-muted">{formatDue(l.next_action_due)}</span>
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function MobileStages({ leads, list }: { leads: LeadRow[]; list: string }) {
  const [stage, setStage] = useState<LeadStage>(BOARD_STAGES[0]!);
  const inStage = leads.filter((l) => l.stage === stage);
  return (
    <div className="md:hidden" data-testid="kanban-mobile">
      <div className="mb-2 flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="Stages">
        {BOARD_STAGES.map((st) => (
          <button
            key={st}
            type="button"
            role="tab"
            aria-selected={st === stage}
            data-testid={`stage-chip-${st}`}
            onClick={() => setStage(st)}
            className={cn(
              "shrink-0 rounded-full border px-3 text-sm min-h-11",
              st === stage ? "border-primary bg-primary/10 font-medium text-primary" : "text-fg-muted",
            )}
          >
            {STAGE_LABEL[st]} <span className="tabular-nums">{leads.filter((l) => l.stage === st).length}</span>
          </button>
        ))}
      </div>
      <LeadCards leads={inStage} list={list} offset={0} />
    </div>
  );
}

/** §7.3 Leads: table with server-side sort/filter/paginate, plus a Kanban by stage. */
export function Leads() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "kanban" ? "kanban" : "table";
  const page = Number(params.get("page") ?? 1);
  // v2.2 P3.1: no sort param = the server's default, hot first then by due. Sending a column here
  // would turn that default off for everyone who never clicked a header.
  const sort = params.get("sort") ?? undefined;
  const hotOnly = params.get("hot") === "1" || params.get("hot") === "true";
  const callList = (["callbacks_due", "no_answer_3"] as const).find((k) => k === params.get("call_list"));
  const order = params.get("order") === "desc" ? "desc" : "asc";
  const q = params.get("q") ?? "";
  const stageFilter = params.getAll("stage");
  // Overview's cards link straight here with these set (§7.2 → §7.3).
  const overdue = params.get("overdue") === "true";
  const stale = params.get("stale") === "true";
  const hasSuggestion = params.get("has_suggestion") === "true";
  const suggestionFilter = params.getAll("suggestion_state");
  // The id the lead page steps through (D-32). Derived from the live search params, so it carries this
  // page's filter and sort verbatim and changes the moment either does.
  const list = listId("leads", params);
  const [pendingDrag, setPendingDrag] = useState<{ lead: LeadRow; target: LeadStage } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const qc = useQueryClient();
  const toast = useToast();

  // v2.2 P3.1/P3.3: one PATCH per lead — the same endpoint, the same ledger row, the same rules as
  // the flame on the lead page; no bulk route exists for a write that is this small.
  const refreshLists = () => {
    void qc.invalidateQueries({ queryKey: ["leads"] });
    void qc.invalidateQueries({ queryKey: ["nav-counts"] });
    void qc.invalidateQueries({ queryKey: ["overview"] });
  };
  const setHot = useMutation({
    mutationFn: ({ ids, hot }: { ids: string[]; hot: boolean }) =>
      Promise.all(ids.map((id) => api.patchLead(id, { hot }))),
    onSuccess: (_d, v) => {
      toast.push(
        v.ids.length === 1
          ? v.hot ? "Marked hot" : "Unmarked hot"
          : `${v.ids.length} leads ${v.hot ? "marked" : "unmarked"} hot`,
        "ok",
        // v2.6 P3: five seconds to take it back.
        { action: { label: "Undo", onClick: () => setHot.mutate({ ids: v.ids, hot: !v.hot }) } },
      );
      refreshLists();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  // v2.6 P3 (D-101): the bulk actions, one request per lead through the same endpoint the lead page
  // uses — the same ledger row and the same stage rules. Dead stays reversible through revive.
  const bulkDead = useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: "no_response" | "not_interested" }) =>
      Promise.all(
        ids.map((id) => api.patchLead(id, { stage: "dead", dead_reason: reason, next_action: null })),
      ),
    onSuccess: (_d, v) => {
      toast.push(`${v.ids.length} lead(s) dead: ${v.reason.replace("_", " ")} — revive from the lead page`);
      setSelected(new Set());
      refreshLists();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  const bulkSweep = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((id) => api.sweepLead(id))),
    onSuccess: (_d, ids) => {
      toast.push(`${ids.length} lead(s) snoozed to Friday's sweep`, "ok", {
        action: {
          label: "Undo",
          onClick: () => {
            void Promise.all(ids.map((id) => api.unsweepLead(id))).then(refreshLists);
          },
        },
      });
      setSelected(new Set());
      refreshLists();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  const bulkAccept = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.allSettled(ids.map((id) => api.acceptSuggestion(id))),
    onSuccess: (results) => {
      const ok = results.filter((r) => r.status === "fulfilled").length;
      toast.push(
        `${ok} suggestion(s) accepted${ok < results.length ? `; ${results.length - ok} had none pending` : ""}`,
        ok ? "ok" : "danger",
      );
      setSelected(new Set());
      refreshLists();
    },
  });

  const bulkTriage = useMutation({
    mutationFn: (ids: string[]) => api.triageLeadsBulk(ids),
    onSuccess: (out) => {
      const parts = [`${out.ok} suggested`];
      if (out.skipped) parts.push(`${out.skipped} skipped`);
      if (out.failed) parts.push(`${out.failed} failed`);
      if (out.refused) parts.push("budget reached");
      toast.push(`${parts.join(" · ")} · $${out.total_cost_usd}`, out.failed || out.refused ? "danger" : "ok");
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ["leads"] });
      void qc.invalidateQueries({ queryKey: ["nav-counts"] });
    },
    onError: (e: Error) => {
      toast.push(e.message, "danger");
    },
  });

  // v2.3 P2.1 (D-77): the sort is remembered in this browser. A URL that names a sort wins; a URL that
  // names none picks up the remembered one once, on arrival.
  useEffect(() => {
    if (params.get("sort")) return;
    const stored = readStoredSort(LEADS_SORT_KEY);
    if (!stored?.sort) return;
    const next = new URLSearchParams(params);
    next.set("sort", stored.sort);
    next.set("order", stored.order);
    setParams(next, { replace: true });
    // Once per arrival: re-running on every param change would undo choosing "Default".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const sortChoice = matchSort(LEAD_SORTS, sort ?? null, order);

  function chooseSort(id: string) {
    const option = LEAD_SORTS.find((o) => o.id === id);
    if (!option) return;
    const next = new URLSearchParams(params);
    if (option.sort) {
      next.set("sort", option.sort);
      next.set("order", option.order);
    } else {
      next.delete("sort");
      next.delete("order");
    }
    next.delete("page");
    setParams(next);
    storeSort(LEADS_SORT_KEY, option.sort, option.order);
  }

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "leads",
      { q, stageFilter, suggestionFilter, overdue, stale, hasSuggestion, hotOnly, callList, sort, order, page, view },
    ],
    queryFn: () =>
      api.leads({
        q: q || undefined,
        stage: stageFilter.length ? stageFilter : undefined,
        suggestion_state: suggestionFilter.length ? suggestionFilter : undefined,
        overdue: overdue || undefined,
        stale: stale || undefined,
        has_suggestion: hasSuggestion || undefined,
        hot: hotOnly || undefined,
        call_list: callList,
        sort,
        order,
        page: view === "kanban" ? 1 : page,
        page_size: view === "kanban" ? 200 : 50,
      }),
  });

  // The three states a select-all can be in. `selectableIds` is the first 25 on the page, which is
  // what "all" means here — never the whole result set.
  const selectableIds = (data?.items ?? []).slice(0, BULK_TRIAGE_MAX).map((l) => l.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const partiallySelected = selected.size > 0 && !allSelected;

  return (
    <div className="p-4">
      {pendingDrag && (
        <StageMoveModal
          lead={pendingDrag.lead}
          target={pendingDrag.target}
          onClose={() => setPendingDrag(null)}
        />
      )}
      <header className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Leads</h1>
          <p className="text-xs text-fg-muted">
            {data ? `${data.total} live lead${data.total === 1 ? "" : "s"}` : "…"} ·{" "}
            <Link to="/archived" className="underline">
              archived
            </Link>{" "}
            ·{" "}
            <Link to="/sms-templates" className="underline" data-testid="leads-sms-templates">
              SMS templates
            </Link>
          </p>
          {(overdue || stale || hasSuggestion || hotOnly) && (
            <div className="mt-1 flex items-center gap-1" data-testid="active-filters">
              {hotOnly && <Badge tone="warn">hot</Badge>}
              {overdue && <Badge tone="danger">overdue</Badge>}
              {stale && <Badge tone="warn">stale</Badge>}
              {hasSuggestion && <Badge tone="info">suggestion pending</Badge>}
              <Link to="/leads" className="ml-1 text-[11px] text-fg-muted underline">
                clear
              </Link>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            defaultValue={q}
            onKeyDown={(e) => {
              if (e.key === "Enter") setParam("q", (e.target as HTMLInputElement).value);
            }}
            placeholder="Filter name, phone, APN…"
            data-testid="leads-filter"
            className={inputClass + " w-56 max-md:w-full"}
          />
          <select
            value={stageFilter[0] ?? ""}
            onChange={(e) => setParam("stage", e.target.value || null)}
            className={inputClass + " w-36 max-md:w-auto max-md:min-w-0 max-md:grow max-md:basis-[40%]"}
            aria-label="Filter by stage"
            data-testid="stage-filter"
          >
            <option value="">All stages</option>
            {/* `dead` is absent: this page never shows one (D-32), so offering it as a filter would
                offer a guaranteed-empty result. /archived is the link that answers that question. */}
            {BOARD_STAGES.map((st) => (
              <option key={st} value={st}>
                {STAGE_LABEL[st]}
              </option>
            ))}
          </select>
          <select
            value={suggestionFilter[0] ?? ""}
            onChange={(e) => setParam("suggestion_state", e.target.value || null)}
            className={inputClass + " w-40 max-md:w-auto max-md:min-w-0 max-md:grow max-md:basis-[40%]"}
            aria-label="Filter by suggestion state"
            data-testid="suggestion-filter"
          >
            <option value="">Any suggestion</option>
            {SUGGESTION_STATES.map((st) => (
              <option key={st} value={st}>
                {SUGGESTION_LABEL[st]}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1 text-xs text-fg-muted">
            Sort
            <select
              value={sortChoice?.id ?? "custom"}
              onChange={(e) => chooseSort(e.target.value)}
              className={inputClass + " w-48 max-md:w-40"}
              aria-label="Sort leads"
              data-testid="leads-sort"
            >
              {LEAD_SORTS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
              {!sortChoice && <option value="custom">Column: {sort} {order === "desc" ? "▼" : "▲"}</option>}
            </select>
          </label>
          <Button
            size="sm"
            variant={hotOnly ? "default" : "ghost"}
            onClick={() => setParam("hot", hotOnly ? null : "1")}
            data-testid="hot-filter"
            aria-pressed={hotOnly}
            className={hotOnly ? "border-orange-500/60 text-orange-700 dark:text-orange-400" : undefined}
          >
            <Flame className="size-3.5" fill={hotOnly ? "currentColor" : "none"} aria-hidden /> Hot
          </Button>
          {/* v2.9 P3.3 (D-127): the call log's two queues */}
          {(
            [
              ["callbacks_due", "Callbacks due"],
              ["no_answer_3", "No answer ×3+"],
            ] as const
          ).map(([key, label]) => (
            <Button
              key={key}
              size="sm"
              variant={callList === key ? "default" : "ghost"}
              aria-pressed={callList === key}
              data-testid={`call-list-${key}`}
              onClick={() => setParam("call_list", callList === key ? null : key)}
            >
              {label}
            </Button>
          ))}
          <div className="flex rounded border">
            <Button
              size="sm"
              variant={view === "table" ? "default" : "ghost"}
              onClick={() => setParam("view", "table")}
              data-testid="view-table"
            >
              Table
            </Button>
            <Button
              size="sm"
              variant={view === "kanban" ? "default" : "ghost"}
              onClick={() => setParam("view", "kanban")}
              data-testid="view-kanban"
            >
              Kanban
            </Button>
          </div>
        </div>
      </header>

      {isLoading && <Spinner />}
      {error && <ErrorNote error={error} />}

      {data && view === "kanban" && (
        <>
          <div className="max-md:hidden">
            <Kanban leads={data.items} list={list} />
          </div>
          <MobileStages leads={data.items} list={list} />
        </>
      )}

      {data && view === "table" && selected.size > 0 && (
        // (the bulk bar belongs to the table's checkboxes, which a phone does not show)
        <div
          className="mb-2 flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border bg-surface px-3 py-2 text-xs"
          data-testid="bulk-bar"
        >
          <span className="font-medium" data-testid="bulk-count">
            {selected.size} selected
          </span>
          <Button
            size="sm"
            onClick={() => bulkTriage.mutate([...selected])}
            disabled={bulkTriage.isPending}
            data-testid="bulk-triage"
          >
            {bulkTriage.isPending ? "Triaging…" : `Triage ${selected.size}`}
          </Button>
          <Button
            size="sm"
            onClick={() => setHot.mutate({ ids: [...selected], hot: true })}
            disabled={setHot.isPending}
            data-testid="bulk-hot"
          >
            <Flame className="size-3.5" aria-hidden /> Mark hot
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setHot.mutate({ ids: [...selected], hot: false })}
            disabled={setHot.isPending}
            data-testid="bulk-unhot"
          >
            Unmark hot
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              // v2.9 P3.3: Sam confirms a bulk Dead; nothing moves a stage on its own (Rule 0).
              if (window.confirm(`Mark ${selected.size} lead${selected.size === 1 ? "" : "s"} Dead — no response?`)) {
                bulkDead.mutate({ ids: [...selected], reason: "no_response" });
              }
            }}
            disabled={bulkDead.isPending}
            data-testid="bulk-dead-no-response"
          >
            Dead: no response
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => bulkDead.mutate({ ids: [...selected], reason: "not_interested" })}
            disabled={bulkDead.isPending}
            data-testid="bulk-dead-not-interested"
          >
            Dead: not interested
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => bulkSweep.mutate([...selected])}
            disabled={bulkSweep.isPending}
            data-testid="bulk-sweep"
          >
            Snooze to Friday sweep
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => bulkAccept.mutate([...selected])}
            disabled={bulkAccept.isPending}
            data-testid="bulk-accept"
          >
            Accept suggestion
          </Button>
          <button
            type="button"
            className="underline"
            onClick={() => setSelected(new Set())}
            data-testid="bulk-clear"
          >
            Clear
          </button>
          <span className="text-fg-muted">
            {selected.size >= BULK_TRIAGE_MAX
              ? `${BULK_TRIAGE_MAX} is the cap per click — it is what stops one click on a bulk import spending the month's budget.`
              : `Up to ${BULK_TRIAGE_MAX} per click. Leads from a live feeder are triaged automatically; bulk-imported rows are not.`}
          </span>
        </div>
      )}

      {data && view === "table" && (
        <LeadCards leads={data.items} list={list} offset={(data.page - 1) * data.page_size} />
      )}
      {data && view === "table" && (
        <Card className="overflow-hidden max-md:hidden">
          {data.items.length === 0 ? (
            <Empty
              title="No leads match."
              hint="Clear the filter, or import a SmarterContact export from /ingest."
            />
          ) : (
            <div className="-mx-3 -my-3 overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b bg-surface-2 text-left">
                    {COLUMNS.map((c) =>
                      c.key === "select" ? (
                        <th key={c.key} className={cn("px-2 py-1.5", c.className)}>
                          {/* Selects the first 25 on the page, not "everything that matches". A
                              select-all that silently meant 652 leads is exactly the click this cap
                              exists to prevent.

                              Properly tri-state. The first version set `checked={selected.size > 0}`,
                              which rendered the box as fully checked when a single row was ticked — so
                              clicking it did nothing (or cleared the selection), and there was no way
                              to get from one row to twenty-five. `indeterminate` is the honest state
                              for a partial selection and has to be set on the DOM node, since React
                              has no prop for it. */}
                          <input
                            type="checkbox"
                            aria-label={`Select up to ${BULK_TRIAGE_MAX} leads on this page`}
                            data-testid="select-all"
                            ref={(node) => {
                              if (node) node.indeterminate = partiallySelected;
                            }}
                            checked={allSelected}
                            onChange={() =>
                              setSelected(
                                allSelected
                                  ? new Set()
                                  : new Set(selectableIds),
                              )
                            }
                          />
                        </th>
                      ) : (
                      <th key={c.key} className={cn("px-2 py-1.5 text-xs font-semibold", c.className)}>
                        {c.sortable ? (
                          <button
                            type="button"
                            onClick={() => {
                              const nextOrder = sort === c.sortable && order === "asc" ? "desc" : "asc";
                              const next = new URLSearchParams(params);
                              next.set("sort", c.sortable!);
                              next.set("order", nextOrder);
                              next.delete("page");
                              setParams(next);
                              // v2.3 P2.1: a header click is a sort choice too; the menu follows it.
                              storeSort(LEADS_SORT_KEY, c.sortable!, nextOrder);
                            }}
                            className="inline-flex items-center gap-1 hover:text-brand"
                          >
                            {c.label}
                            {sort === c.sortable && <span>{order === "asc" ? "▲" : "▼"}</span>}
                          </button>
                        ) : (
                          c.label
                        )}
                      </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((l, index) => (
                    <tr key={l.id} className="border-b last:border-b-0 hover:bg-surface-2" data-testid="lead-row">
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          aria-label={`Select ${l.first_name ?? ""} ${l.last_name ?? ""}`.trim()}
                          data-testid="lead-select"
                          checked={selected.has(l.id)}
                          onChange={(e) =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) {
                                // Refuse the 26th rather than silently dropping it later.
                                if (next.size >= BULK_TRIAGE_MAX) return prev;
                                next.add(l.id);
                              } else {
                                next.delete(l.id);
                              }
                              return next;
                            })
                          }
                        />
                      </td>
                      <td className="px-1 py-1.5">
                        <HotToggle
                          hot={l.hot ?? false}
                          onToggle={(hot) => setHot.mutate({ ids: [l.id], hot })}
                          testId={`hot-toggle-${l.id}`}
                        />
                      </td>
                      <td className="max-w-48 truncate px-2 py-1.5">
                        {/* The link carries the row's place in THIS list, so the lead page can offer
                            ← / → through it and say "n of N" (D-32). */}
                        <Link
                          data-testid="lead-link"
                          to={leadHref(l.id, list, (data.page - 1) * data.page_size + index)}
                          className="font-medium hover:underline"
                        >
                          {leadName(l)}
                        </Link>
                      </td>
                      <td className="px-2 py-1.5 tabular-nums text-fg-muted">
                        <PhoneLink e164={l.phone_e164} compact leadId={l.id} surface="leads-table" />
                      </td>
                      <td className="px-2 py-1.5">{shown(l.market)}</td>
                      <td className="px-2 py-1.5">{shown(l.parcel_county)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatAcres(l.parcel_acres)}
                      </td>
                      <td className="px-2 py-1.5 text-xs">{l.property_type ?? "—"}</td>
                      <td className="px-2 py-1.5">
                        <Badge tone={stageTone(l.stage)}>{STAGE_LABEL[l.stage]}</Badge>
                      </td>
                      <td className="px-2 py-1.5">{l.tier ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{l.motivation_score}</td>
                      <td className="px-2 py-1.5 text-xs">{l.reply_class ?? "—"}</td>
                      <td className="px-2 py-1.5 text-xs">
                        {l.suggestion_state === "none" ? (
                          <span className="text-fg-muted">—</span>
                        ) : (
                          <Badge tone={suggestionTone(l.suggestion_state)}>
                            {SUGGESTION_LABEL[l.suggestion_state]}
                          </Badge>
                        )}
                      </td>
                      <td className="max-w-56 truncate px-2 py-1.5 text-xs">{l.next_action ?? "—"}</td>
                      <td className="px-2 py-1.5 text-xs tabular-nums">
                        <span
                          className={
                            // v2.1 T5.4: the ingest placeholder waiting on a pending suggestion is not
                            // work, so it is not painted overdue — the same rule as
                            // public.lead_action_is_actionable, which the panels and the nav count use.
                            l.next_action_due &&
                            new Date(l.next_action_due) < new Date() &&
                            isActionable(l.next_action, l.suggestion_state)
                              ? "text-danger"
                              : ""
                          }
                          data-testid="lead-due"
                        >
                          {formatDue(l.next_action_due)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-xs tabular-nums">
                        {l.stale ? <Badge tone="warn">stale</Badge> : formatAgo(l.last_touched_at)}
                      </td>
                      <td className="px-2 py-1.5 text-xs">{l.campaign ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {data && view === "table" && data.pages > 1 && (
        <div className="mt-2 flex items-center justify-between text-xs text-fg-muted">
          <span>
            Page {data.page} of {data.pages}
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              disabled={data.page <= 1}
              onClick={() => setParam("page", String(data.page - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              disabled={data.page >= data.pages}
              onClick={() => setParam("page", String(data.page + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
